import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { getMaintenanceComparison } from "@/lib/maintenance-topic.functions";
import type { MaintenanceBrandRow } from "@/lib/maintenance-topic.server";
import { breadcrumbLd, faqLd, itemListLd, pageTitle, SITE_OG_IMAGE } from "@/lib/seo";
import { NOINDEX_META } from "@/lib/index-policy";
import { FaqBlock } from "@/components/FaqBlock";
import { TOPICS } from "@/lib/topics-config";

const URL_PATH = "https://autospecdata.com/maintenance-comparison";
const H1 = "Maintenance Intervals Compared by Brand";
const TITLE = "Car Maintenance Interval Comparison by Brand";

const FAQS = [
  {
    q: "Which brands have the longest oil change intervals?",
    a: "Brands whose engines are predominantly six-cylinder or larger, and most luxury marques, publish 10,000-mile oil intervals because they specify full-synthetic oil from the factory. Mainstream four-cylinder brands land at 7,500 miles. Battery-electric brands have no oil service at all.",
  },
  {
    q: "Does following a longer interval void my warranty?",
    a: "No — following the manufacturer's published interval is exactly what the warranty requires. What voids coverage is missing services entirely or using an oil grade the manufacturer does not specify. Keep receipts; a dealer can ask for proof of scheduled service during a powertrain claim.",
  },
  {
    q: "Why do electric brands still need scheduled service?",
    a: "EVs drop oil changes, spark plugs and belts, but they add tire wear from instant torque and battery weight, brake fluid that absorbs moisture on a calendar rather than a mileage basis, and a high-voltage coolant loop that determines how the pack ages. Tire rotation is the service EV owners skip most and regret most.",
  },
  {
    q: "Should I use the severe-duty schedule instead?",
    a: "Most drivers qualify for severe duty without realising it: short trips under 10 miles, stop-and-go traffic, towing, dusty roads, or sustained sub-freezing temperatures. If two or more apply to you, halve the oil interval shown here and keep everything else on schedule.",
  },
];

function money(row: MaintenanceBrandRow) {
  return `$${row.costLow}–$${row.costHigh}`;
}

function miles(n: number) {
  return n === 0 ? "—" : `${n.toLocaleString("en-US")} mi`;
}

export const Route = createFileRoute("/maintenance-comparison")({
  loader: () => getMaintenanceComparison(),
  head: ({ loaderData }) => {
    if (!loaderData || loaderData.rows.length === 0) {
      return { meta: [{ title: pageTitle(TITLE) }, ...NOINDEX_META] };
    }
    const description = `Oil change, tire rotation and major service intervals compared across ${loaderData.brandCount} car brands, with typical independent-shop costs for each.`;
    return {
      meta: [
        { title: pageTitle(TITLE) },
        { name: "description", content: description },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: description },
        { property: "og:url", content: URL_PATH },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: URL_PATH }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            itemListLd(
              H1,
              loaderData.rows.map((r) => ({
                name: `${r.makeName} maintenance schedule`,
                path: `/${r.makeSlug}/maintenance-schedule`,
              })),
            ),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqLd(FAQS)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: H1, path: URL_PATH },
            ]),
          ),
        },
      ],
    };
  },
  component: MaintenanceComparisonPage,
});

type SortKey = "makeName" | "oilMiles" | "rotationMiles" | "majorServiceMiles" | "costLow";

function MaintenanceComparisonPage() {
  const data = Route.useLoaderData();
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "makeName",
    dir: "asc",
  });

  if (!data || data.rows.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-foreground">{H1}</h1>
        <p className="mt-4 text-muted-foreground">
          This comparison is being rebuilt. Check back shortly.
        </p>
      </div>
    );
  }

  const rows =
    sort.key === "makeName" && sort.dir === "asc"
      ? data.rows
      : [...data.rows].sort((a, b) => {
          const diff =
            sort.key === "makeName"
              ? a.makeName.localeCompare(b.makeName)
              : (a[sort.key] as number) - (b[sort.key] as number);
          return sort.dir === "asc" ? diff : -diff;
        });

  const evBrands = data.rows.filter((r) => r.isElectric).length;
  const longOil = data.rows.filter((r) => r.oilMiles >= 10000).length;

  const answer = `Across ${data.brandCount} brands, ${longOil} publish a 10,000-mile oil interval and the rest specify 7,500 miles, while ${evBrands} battery-electric brands have no oil service at all. Tire rotation lands between 5,000 and 7,500 miles almost everywhere, and the first major milestone service falls between 60,000 and 100,000 miles.`;

  function SortButton({ label, sortKey }: { label: string; sortKey: SortKey }) {
    const active = sort.key === sortKey;
    return (
      <button
        type="button"
        onClick={() =>
          setSort((s) =>
            s.key === sortKey
              ? { key: sortKey, dir: s.dir === "asc" ? "desc" : "asc" }
              : { key: sortKey, dir: "asc" },
          )
        }
        aria-label={`Sort by ${label}`}
        className="inline-flex items-center gap-1 font-semibold text-foreground hover:text-primary"
      >
        {label}
        <span aria-hidden="true" className={active ? "opacity-100" : "opacity-30"}>
          {active && sort.dir === "desc" ? "▼" : "▲"}
        </span>
      </button>
    );
  }

  return (
    <div>
      <nav className="mx-auto max-w-5xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{H1}</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{H1}</h1>
          <aside
            data-aeo="atomic-passage"
            aria-label="Quick answer summary"
            className="mt-4 max-w-3xl rounded-xl border border-border/80 bg-muted/40 p-4 shadow-sm"
          >
            <p className="text-base leading-relaxed text-foreground">{answer}</p>
          </aside>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
            Maintenance is the one running cost you control, and the gap between brands is wider
            than most buyers expect. A four-cylinder mainstream car on a 7,500-mile oil interval
            with a $70 to $160 service costs a fraction of a luxury V6 on 10,000-mile synthetic
            with a $140 to $380 visit — even though the luxury car goes longer between shop trips.
            Each interval below is derived from the powertrain mix actually recorded in our
            database for that brand, not from a generic template.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <p className="mb-3 text-xs text-muted-foreground">
          Tap any column heading to re-sort the table.
        </p>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="rank-table">
            <caption className="sr-only">
              Maintenance intervals and typical service cost for {data.brandCount} car brands
            </caption>
            <thead className="bg-secondary/60">
              <tr>
                <th scope="col">
                  <SortButton label="Brand" sortKey="makeName" />
                </th>
                <th scope="col">
                  <SortButton label="Oil change" sortKey="oilMiles" />
                </th>
                <th scope="col" data-hide="sm">
                  <SortButton label="Tire rotation" sortKey="rotationMiles" />
                </th>
                <th scope="col" data-hide="md">
                  <SortButton label="Major service" sortKey="majorServiceMiles" />
                </th>
                <th scope="col" data-hide="md">
                  <SortButton label="Typical shop cost" sortKey="costLow" />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.makeSlug}>
                  <td>
                    <Link
                      to="/$make/maintenance-schedule"
                      params={{ make: r.makeSlug }}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {r.makeName}
                    </Link>
                    {r.isElectric && (
                      <span className="ml-2 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                        EV
                      </span>
                    )}
                  </td>
                  <td data-strong="">{miles(r.oilMiles)}</td>
                  <td data-hide="sm">
                    {miles(r.rotationMiles)}
                  </td>
                  <td data-hide="md">
                    {miles(r.majorServiceMiles)}
                  </td>
                  <td data-hide="md">
                    {money(r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
          Intervals shown are the normal-duty schedule. Costs are typical independent-shop pricing
          for a routine visit and exclude brake, tyre and timing-component work. Dealer pricing
          runs roughly 30 to 60 percent higher, and prepaid maintenance plans usually cover only
          the oil and rotation line items, not the fluid and filter services that appear at the
          major milestones.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-10">
        <h2 className="text-lg font-semibold text-foreground mb-4">Frequently asked questions</h2>
        <FaqBlock items={FAQS} />
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-12 border-t border-border pt-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">Other spec charts</h2>
        <ul className="flex flex-wrap gap-2">
          {TOPICS.map((t) => (
            <li key={t.slug}>
              <Link
                to={t.path}
                className="chip-link"
              >
                {t.h1}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/car-maintenance-schedule"
              className="chip-link"
            >
              Generic maintenance schedule
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
